import { useState } from 'react';
import { NJButton, NJHeading, NJTab, NJTabs, NJText, NJInlineMessage } from '@engie-group/fluid-design-system-react';
import { UMFormStyles } from '../../urbanmap/_components';
import { isValidContract } from './domain';
import { isPending, type SettingsDraft } from './configDraft';
import { SettingsPerimeterTab } from './SettingsPerimeterTab';
import { SettingsRatiosTab } from './SettingsRatiosTab';
import type { ConfigurationReviewTab, PlanBuilding, PlanProject, Referential } from './types';
import './configuration.css';

export type { SettingsDraft } from './configDraft';

interface Props {
  project: PlanProject;
  buildings: PlanBuilding[];
  referential: Referential;
  draft: SettingsDraft;
  onDraftChange: (patch: Partial<SettingsDraft>) => void;
  dirty: boolean;
  saving: boolean;
  revision: number;
  saveError: boolean;
  onSave: () => void;
  onCancel: () => void;
  onClose: () => void;
}

/** Erreur bloquante, dans l'ordre des rubriques de l'assistant de création. */
function blocking(d: SettingsDraft, ratiosValid: boolean, editing: boolean): string | null {
  if (!d.name.trim()) return 'Donnez un nom au projet.';
  if (d.kind === 'extension' && d.networkIds.length === 0) return 'Sélectionnez au moins un réseau existant.';
  if (!isValidContract(d.contract)) return 'Vérifiez la période du contrat.';
  if (!ratiosValid) return 'Corrigez les ratios hors plage.';
  if (editing) return 'Terminez l’édition des ratios.';
  if (isPending(d)) return 'Appliquez mes ratios au projet avant d’enregistrer.';
  return null;
}

/** Pleine page, mêmes paramètres que l'assistant de création ; mêmes droits pour tous (PO, 30/10/2026). */
export function SettingsPanel(p: Props) {
  const [tab, setTab] = useState<ConfigurationReviewTab>('perimeters');
  const [ratiosValid, setRatiosValid] = useState(true);
  const [editing, setEditing] = useState(false);
  const error = blocking(p.draft, ratiosValid, editing);

  return (
    <section className="pnv2-config" aria-label="Configuration projet" data-comment-view="configuration">
      <UMFormStyles />
      <header className="pnv2-config-header">
        <NJHeading as="h1" scale="md">Configuration projet</NJHeading>
        <NJButton label="Fermer la configuration" icon="close" variant="secondary"
          disabled={p.saving} onClick={p.onClose} />
      </header>
      <main className="pnv2-config-main">
        <NJTabs label="Configuration projet" activeTab={tab}
          onClickTabItem={(id) => setTab(id === 'ratios' ? 'ratios' : 'perimeters')}>
          <NJTab id="perimeters" label="Périmètres" ariaControls="config-perimeters-panel">
            <fieldset className="pnv2-config-body" disabled={p.saving}>
              <SettingsPerimeterTab project={p.project} buildings={p.buildings} referential={p.referential}
                draft={p.draft} onDraftChange={p.onDraftChange} />
            </fieldset>
          </NJTab>
          <NJTab id="ratios" label="ratios de consommations" ariaControls="config-ratios-panel">
            <fieldset className="pnv2-config-body" disabled={p.saving}>
              <SettingsRatiosTab key={`ratios-${p.revision}`} buildings={p.buildings} referential={p.referential}
                perimeter={p.project.perimeter}
                draft={p.draft} onDraftChange={p.onDraftChange}
                onValidity={setRatiosValid} onEditingChange={setEditing} />
            </fieldset>
          </NJTab>
        </NJTabs>
      </main>
      <footer className="pnv2-config-footer">
        <div className="pnv2-config-status" aria-live="polite">
          {p.saveError ? <NJInlineMessage variant="error" role="alert"><NJText scale="sm">Échec de l’enregistrement. Vos modifications sont conservées ; réessayez.</NJText></NJInlineMessage>
            : p.dirty ? <NJText scale="sm">Modifications non enregistrées</NJText>
                : <NJText scale="sm">Aucune modification</NJText>}
          {p.dirty && error && <NJText role="alert" scale="sm">{error}</NJText>}
          <NJText scale="xs">Les consommations du projet changent à l’enregistrement.</NJText>
        </div>
        <NJButton label="Annuler" variant="secondary" disabled={p.saving || (!p.dirty && !editing && ratiosValid)}
          onClick={() => { setEditing(false); setRatiosValid(true); p.onCancel(); }} />
        <NJButton label={p.saving ? 'Enregistrement…' : 'Enregistrer'} icon="save"
          disabled={!p.dirty || !!error || p.saving} onClick={p.onSave} />
      </footer>
    </section>
  );
}
